// notifyAdjusterReady — fires when a deal advances to adjuster_meeting_ready
// via "PRE Done" (the pre-inspection is complete and the deal now needs an
// adjuster meeting scheduled).
//
// Inserts in-app notifications for the RM (deal owner), the split RM (if any),
// and the assigned roof specialist — deduped so nobody gets two. The body tells
// the recipient whether the adjuster meeting date/time is already on file or
// still needs to be scheduled, which is the whole point of the alert.
//
// In-app only (no SMS) per Greg (Jun 8, 2026). Fire-and-forget: any failure is
// logged via console.warn and never blocks the caller. Called from PreDoneForm
// (StageTransitionModal) and the RoofMaster "PRE Done" button — both UI routes
// into adjuster_meeting_ready via PRE Done. Manual SQL writes won't fire it.

import { supabase } from "./supabase";
import type { Deal } from "./types";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";

type DealForNotify = Pick<
  Deal,
  "id" | "homeowner_name" | "property_street" | "property_city" |
  "owner_id" | "split_rm_id" | "assigned_rs_id" | "adjuster_meeting_date"
>;

/**
 * Format the adjuster meeting date for the notification body.
 *
 * adjuster_meeting_date is stored as "fake UTC" — Eastern wall-clock digits
 * tagged +00:00 (Zoho migration convention). We read the raw YYYY-MM-DD / HH:mm
 * digits directly rather than constructing a Date, which would shift them by the
 * browser TZ offset. Returns null when no date is set.
 */
function formatAdjusterWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, , mo, d, hh, mm] = m;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthLabel = months[Number(mo) - 1] ?? mo;
  let hour = Number(hh);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${monthLabel} ${Number(d)} at ${hour}:${mm} ${ampm}`;
}

/**
 * Fire-and-forget. Caller should NOT await unless they need confirmation.
 * Errors are swallowed and logged via console.warn — never bubble to the UI.
 */
export async function notifyAdjusterReady(deal: DealForNotify): Promise<void> {
  try {
    // Recipients: owner + split RM + assigned RS, deduped, non-null.
    const recipients = Array.from(
      new Set(
        [deal.owner_id, deal.split_rm_id, deal.assigned_rs_id].filter(
          (id): id is string => !!id,
        ),
      ),
    );
    if (recipients.length === 0) {
      console.warn("[notifyAdjusterReady] no recipients (owner/split/RS all null) — skipping.");
      return;
    }

    const homeowner = deal.homeowner_name || "(no name)";
    const dealUrl = `${PORTAL_BASE_URL}/deal/${deal.id}`;
    const when = formatAdjusterWhen(deal.adjuster_meeting_date);
    const body = when
      ? `PRE complete. Adjuster meeting set for ${when}.`
      : "PRE complete. ⚠ No adjuster meeting date/time set yet — schedule it.";

    const rows = recipients.map((uid) => ({
      user_id: uid,
      type: "adjuster_ready",
      title: `Adjuster meeting ready — ${homeowner}`,
      body,
      deal_id: deal.id,
      metadata: { url: dealUrl, adjuster_scheduled: !!when },
    }));

    const { error } = await supabase.from("notifications").insert(rows);
    if (error) {
      console.warn("[notifyAdjusterReady] notification insert failed:", error.message);
    }
  } catch (err) {
    console.warn("[notifyAdjusterReady] unexpected error:", err);
  }
}
